import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { authApi, planosApi } from '../../api/endpoints'
import { useAuthStore } from '../../store/authStore'
import { Input, PasswordInput } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { BotaoGoogle } from '../../components/BotaoGoogle'
import { formatMoney, cn } from '../../lib/utils'
import type { LoginResponse } from '../../types'
import logoLeao from '../../assets/logo-leao.png'

export function CriarContaPage() {
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)

  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [aceito, setAceito] = useState(false)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const { data: config } = useQuery({ queryKey: ['auth-config'], queryFn: authApi.configuracao, retry: false })
  const { data: planos = [] } = useQuery({ queryKey: ['planos'], queryFn: planosApi.list })

  const diasTrial = config?.diasDeTrial ?? 30
  const premium = planos.find((p) => p.codigo === 'PREMIUM')

  function entrar(data: LoginResponse) {
    setAuth({
      userId: data.user.id,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      userName: data.user.name,
      userEmail: data.user.email,
      userRole: data.user.roles[0],
    })
    navigate('/dashboard')
  }

  async function criarComEmail(e: FormEvent) {
    e.preventDefault()
    setErro(null)

    if (!aceito) {
      setErro('Aceite os termos de uso para continuar.')
      return
    }

    setLoading(true)
    try {
      entrar(await authApi.registrar({ nome, email, password: senha }))
    } catch (e) {
      setErro((e as Error).message || 'Não foi possível criar a conta.')
    } finally {
      setLoading(false)
    }
  }

  async function entrarComGoogle(idToken: string) {
    setErro(null)
    setLoading(true)
    try {
      entrar(await authApi.google(idToken))
    } catch (e) {
      setErro((e as Error).message || 'Não foi possível entrar com o Google.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-bg">
      <div
        className="absolute inset-x-0 top-0 h-[380px] bg-sidebar"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,179,0,0.18), transparent 45%), radial-gradient(circle at 85% 0%, rgba(255,106,0,0.14), transparent 40%)' }}
        aria-hidden
      />

      <div className="relative flex flex-1 flex-col items-center px-4 pt-12 pb-8">
        <Link to="/" className="mb-8 text-center">
          <span className="inline-flex flex-col items-center gap-2">
            <img src={logoLeao} alt="" className="h-20 w-auto" />
            <span className="text-xl font-bold tracking-[0.2em] text-white">WALLEON</span>
          </span>
        </Link>

        <div className="grid w-full max-w-4xl gap-6 lg:grid-cols-[1fr_340px]">
          <div className="rounded bg-surface p-6 shadow-dropdown sm:p-8">
            <div className="mb-6">
              <h5 className="text-xl font-semibold text-white">Criar sua conta</h5>
              <p className="mt-1 text-13 text-muted">
                Comece grátis. Você ganha <strong className="text-primary">{diasTrial} dias de Premium</strong> para testar tudo, sem cartão.
              </p>
            </div>

            {config?.googleHabilitado && config.googleClientId && (
              <div className="mb-5">
                <BotaoGoogle clientId={config.googleClientId} onCredential={entrarComGoogle} texto="signup_with" />
                <div className="my-5 flex items-center gap-3">
                  <span className="h-px flex-1 bg-border" />
                  <span className="text-2xs uppercase text-muted">ou com e-mail</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              </div>
            )}

            <form onSubmit={criarComEmail} className="space-y-4">
              <Input label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Como quer ser chamado" required autoComplete="name" />
              <Input label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" required autoComplete="email" />
              <PasswordInput label="Senha" value={senha} onChange={(e) => setSenha(e.target.value)} placeholder="Mínimo de 8 caracteres" required autoComplete="new-password" />
              <p className="-mt-2 text-2xs text-muted">Use ao menos 8 caracteres, com maiúscula, minúscula, número e símbolo.</p>

              <label className="flex cursor-pointer items-start gap-2">
                <input type="checkbox" checked={aceito} onChange={(e) => setAceito(e.target.checked)} className="mt-0.5 h-4 w-4 rounded accent-primary" />
                <span className="text-13 text-muted">Li e aceito os termos de uso e a política de privacidade.</span>
              </label>

              {erro && (
                <div className="flex items-center gap-2 rounded bg-expense/10 px-3 py-2.5 text-13 text-expense" role="alert">
                  <i className="ri-error-warning-line" aria-hidden /> {erro}
                </div>
              )}

              <Button type="submit" loading={loading} className="w-full">Criar conta grátis</Button>
            </form>

            <p className="mt-5 text-center text-13 text-muted">
              Já tem conta? <Link to="/" className="text-primary hover:underline">Entrar</Link>
            </p>
          </div>

          <div className="rounded bg-surface p-6 shadow-dropdown">
            <h6 className="text-15 font-semibold text-white">O que você leva</h6>
            <p className="mt-1 text-2xs text-muted">Durante o teste, o plano Premium fica liberado.</p>

            <ul className="mt-4 space-y-2.5">
              {[
                'Contas e cartões ilimitados',
                'Orçamento por categoria com alertas',
                'Relatórios completos e exportação',
                'Importação de notas fiscais',
                'Compartilhar a conta com outra pessoa',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-13 text-muted">
                  <i className="ri-check-line mt-0.5 text-income" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-5 border-t border-border/60 pt-4">
              <p className="text-2xs uppercase text-muted">Depois do teste</p>
              <p className="text-13 text-white">
                Continue no <strong>Grátis</strong>, sem pagar nada, ou assine o Premium
                {premium ? <> por <strong className="text-primary">{formatMoney(premium.precoMensal)}</strong> por mês</> : null}.
              </p>
              <p className={cn('mt-2 text-2xs text-muted')}>Você escolhe na hora que quiser, e pode cancelar sozinho.</p>
            </div>
          </div>
        </div>
      </div>

      <footer className="relative pb-6 text-center text-13 text-muted">
        {new Date().getFullYear()} © Walleon. Todos os direitos reservados.
      </footer>
    </div>
  )
}
